// ERE-418: retained, read-only metadata for saved legacy strategies.
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const CLASS_LABEL = {
  directional_equity: 'Equity',
  directional_options: 'Options',
  hedging: 'Hedge',
  income_spreads: 'Spreads',
  multi_instrument: 'Multi',
}

const STAGE_DOT = {
  idea: 'bg-inkMuted/40',
  draft: 'bg-blue-400',
  structured: 'bg-blue-400',
  parameterized: 'bg-blue-400',
  validated: 'bg-forest',
  deployed: 'bg-forest',
  evolving: 'bg-amber-400',
}

const DIRECTION_LABEL = {
  long: 'LONG',
  short: 'SHORT',
  both: 'BOTH',
  hedge: 'HEDGE',
  neutral: 'NEUTRAL',
}

function relativeTime(iso) {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diff = now - then
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

export default function StrategyLibrary() {
  const { user } = useAuth()
  const [strategies, setStrategies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setStrategies([])
    setError(null)
    if (!user) {
      setLoading(false)
      return
    }
    setLoading(true)
    ;(async () => {
      try {
        const { data, error: readError } = await supabase
          .from('strategies')
          .select('id, name, strategy_class, direction, stage, version, updated_at')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })
        if (readError) throw readError
        if (!cancelled) setStrategies(data || [])
      } catch {
        if (!cancelled) setError('Saved strategies could not be loaded. Please try again later.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [user])

  return (
    <div className="px-6 py-5 max-w-screen-xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-ink">Saved legacy strategies</h1>
        <p className="text-sm text-inkMuted mt-2">
          The legacy Strategy Workspace is retired. This library is read-only;
          your saved strategies, versions, and results are preserved.
        </p>
      </div>
      {!user ? (
        <p className="text-sm text-inkMuted">Sign in to view your saved legacy strategies.</p>
      ) : loading ? (
        <div role="status" className="font-mono text-xs text-inkMuted animate-pulse">Loading saved strategies...</div>
      ) : error ? (
        <p role="alert" className="text-sm text-ember">{error}</p>
      ) : strategies.length === 0 ? (
        <div className="bg-canvas border border-border rounded-xl py-16 text-center text-inkMuted">
          No saved legacy strategies
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {strategies.map(s => {
            const isDeployed = s.stage === 'deployed'
            const cardCls = `relative group bg-canvas border rounded-xl transition-colors ${
              isDeployed ? 'border-forest/30 hover:border-ink/30' : 'border-border hover:border-ink/30'
            }`
            return (
              <div key={s.id} className={cardCls}>
                <Link to={`/strategies/${s.id}`} className="block p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="font-semibold text-ink text-sm leading-tight group-hover:text-ink truncate pr-8 flex-1">
                      {s.name || 'Untitled Strategy'}
                    </div>
                    <div className="font-mono text-[8px] text-inkMuted/60 shrink-0">v{s.version}</div>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-mono text-[8px] px-1.5 py-px rounded bg-white/5 border border-white/10 text-inkMuted uppercase tracking-widest">
                      {CLASS_LABEL[s.strategy_class] || s.strategy_class}
                    </span>
                    <span className="font-mono text-[8px] px-1.5 py-px rounded bg-white/5 border border-white/10 text-inkMuted uppercase tracking-widest">
                      {DIRECTION_LABEL[s.direction] || s.direction}
                    </span>
                    <span className="font-mono text-[8px] px-1.5 py-px rounded bg-white/5 border border-white/10 text-inkMuted uppercase tracking-widest flex items-center gap-1">
                      <span className={`w-1.5 h-1.5 rounded-full inline-block ${STAGE_DOT[s.stage] || 'bg-inkMuted/40'}`} />
                      {s.stage}
                    </span>
                  </div>
                  <div className="font-mono text-[9px] text-inkMuted/70">Updated {relativeTime(s.updated_at)}</div>
                </Link>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
